import { useNotes } from "../../api/hooks/useNotes";
import { useTags } from "../../api/hooks/useTags";
import type { components } from "../../api/schema";
import { formatDateTime } from "../../lib/date";
import { Skeleton } from "../ui/skeleton";
import { TradeNoteEditor } from "./TradeNoteEditor";

type TradeResponse = components["schemas"]["TradeResponse"];
type NoteResponse = components["schemas"]["NoteResponse"];

// The interactive Notes tab on the trade detail surface, redesigned for the
// single-note model (ADR 0036 D3/D4): one notepad-style note per trade with no
// explicit save. The notepad binds to the trade's oldest live note (== the
// upsert write target); clearing it deletes the note.
//
// A U4-era trade may still carry more than one live trade note. The upsert path
// never auto-deletes the surplus (convergence to exactly one is owned by the
// U5e backfill), so any extras are shown read-only beneath the notepad rather
// than silently hidden -- nothing the operator wrote disappears.
//
// U4d primary-note pinning is retired here (ADR 0036 D5): the pin controls are
// gone; the `trades.primary_note_id` column and routes stay inert until the U5f
// contract-phase drop. The legacy free-text `trade.notes` string stays read-only
// as a "Legacy" card until the U5e backfill migrates it into a Note.
export function TradeNotesPanel({ trade }: { trade: TradeResponse }) {
  const tradeId = trade.trade_id;
  const notesQuery = useNotes({
    note_type: "trade",
    trade_id: tradeId,
    sort_key: "created_at",
    sort_order: "asc",
  });
  const tagsQuery = useTags();

  const tags = tagsQuery.data?.items ?? [];
  const tagName = (tagId: string): string => {
    const tag = tags.find((t) => t.tag_id === tagId);
    if (tag === undefined) return tagId.slice(0, 8);
    return tag.is_deleted ? `${tag.name} (deleted)` : tag.name;
  };

  const legacy = trade.notes.trim();
  const notes = notesQuery.data?.items ?? [];
  // The notepad owns notes[0] (oldest live). Everything after it is U4-era
  // surplus rendered read-only and non-destructively.
  const extras = notes.slice(1);

  return (
    <div className="space-y-3" data-testid="trade-notes-panel">
      <TradeNoteEditor tradeId={tradeId} />

      {legacy !== "" ? (
        <article className="rounded-[11px] border border-dashed border-outline-variant bg-surface-container-low p-3.5">
          <header className="mb-1.5 flex items-center gap-2">
            <span className="rounded-[6px] bg-surface-container-low px-2 py-[3px] text-[9px] font-bold uppercase tracking-[0.06em] text-on-surface-muted">
              Legacy
            </span>
          </header>
          <p className="whitespace-pre-wrap text-sm text-on-surface">{trade.notes}</p>
        </article>
      ) : null}

      {notesQuery.isLoading ? (
        <Skeleton className="h-16 w-full rounded-[11px]" />
      ) : notesQuery.isError ? (
        <p role="alert" className="text-sm text-danger">
          Couldn&apos;t load this trade&apos;s notes.
        </p>
      ) : extras.length > 0 ? (
        <section className="space-y-2" data-testid="trade-notes-extras">
          <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-on-surface-muted">
            Additional notes (read-only)
          </p>
          <ul className="space-y-2">
            {extras.map((note) => (
              <li key={note.note_id}>
                <ReadOnlyNoteCard note={note} tagName={tagName} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// A surplus U4-era trade note shown read-only beneath the notepad. No edit, pin,
// favorite, or delete affordances -- the single notepad above is the only editor.
function ReadOnlyNoteCard({
  note,
  tagName,
}: {
  note: NoteResponse;
  tagName: (tagId: string) => string;
}) {
  return (
    <article
      data-testid={`trade-note-${note.note_id}`}
      className="rounded-[11px] border border-outline-variant bg-surface-container p-3.5"
    >
      <p className="whitespace-pre-wrap text-sm text-on-surface">{note.body}</p>
      {note.tag_ids.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {note.tag_ids.map((tagId) => (
            <li
              key={tagId}
              className="rounded-[9px] border border-outline-variant bg-surface-container-high px-1.5 py-0.5 text-[10px] font-semibold text-on-surface-variant before:text-outline before:content-['#']"
            >
              {tagName(tagId)}
            </li>
          ))}
        </ul>
      ) : null}
      <footer className="mt-2 font-mono text-[10.5px] text-on-surface-muted">
        Updated {formatDateTime(note.updated_at)}
      </footer>
    </article>
  );
}
